import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDollarSign, faSparkles } from '@fortawesome/pro-regular-svg-icons'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import { ButtonV2 } from '@labrav/react-components'
import { useLabraChat, GradientFaIcon, useTrackEvent } from '@labrav/common'
import { FundingRevenueMetric, FundingRevenueSummary } from '../../types'
import { formatAmount } from '../../utils/format'
import { useStyles } from './RevenueSummary.styles'

interface RevenueSummaryProps {
  revenue?: FundingRevenueSummary | null
  loading?: boolean
}

interface MetricProps {
  label: string
  metric: FundingRevenueMetric
  currency: string
  tooltip: string
  loading?: boolean
  onAskAi: () => void
}

const Metric = ({
  label,
  metric,
  currency,
  tooltip,
  loading,
  onAskAi,
}: MetricProps) => {
  const { classes } = useStyles()
  return (
    <Tooltip title={loading ? '' : tooltip} arrow placement="top">
      <button
        type="button"
        className={classes.metric}
        data-testid={`revenue-metric-${label}`}
        aria-label={`${ASK_AI_LABEL}: ${label}`}
        disabled={loading}
        onClick={onAskAi}
      >
        <span className={classes.askAiIconWrap}>
          <GradientFaIcon icon={faSparkles} className={classes.askAiIcon} />
        </span>
        <Typography className={classes.metricLabel}>{label}</Typography>
        <div className={classes.valueRow}>
          {loading ? (
            <CircularProgress size={16} thickness={5} />
          ) : (
            <>
              <Typography className={classes.count}>{metric.count}</Typography>
              <span className={classes.divider} />
              <div className={classes.amountGroup}>
                <Typography component="span" className={classes.currency}>
                  {currency}
                </Typography>
                <Typography component="span" className={classes.amount}>
                  {formatAmount(metric.amount)}
                </Typography>
              </div>
            </>
          )}
        </div>
      </button>
    </Tooltip>
  )
}

const currencySymbol = (currency: string) =>
  currency === 'USD' ? '$' : currency

const CREATE_REQUEST_LABEL = 'Create a new funding request'

const ASK_AI_LABEL = 'Ask Labra AI'

const POTENTIAL_FUNDING_PROMPT =
  'show me my potential fundings in markdown table format with company name, opp id, funding program and amount'

const APPROVED_FUNDING_PROMPT =
  'show me my approved fundings in a table format with company name, opp id, funding program and amount'

const PENDING_FUNDING_PROMPT =
  'show me my pending fundings in a table format with company name, opp id, funding program and amount'

export const RevenueSummary = ({ revenue, loading }: RevenueSummaryProps) => {
  const { classes } = useStyles()
  const { open, startNewChat, sendPrompt } = useLabraChat()
  const symbol = currencySymbol(revenue?.currency ?? 'USD')
  const approved = revenue?.approved ?? { count: 0, amount: 0 }
  const pending = revenue?.pending ?? { count: 0, amount: 0 }
  const potential = revenue?.potential ?? { count: 0, amount: 0 }

  const trackEvent = useTrackEvent()

  const askAi = (prompt: string, component?: string) => () => {
    if (component) trackEvent({ component, action: 'clicked' })
    open()
    startNewChat()
    sendPrompt(prompt)
  }

  return (
    <section className={classes.card} data-testid="revenue-summary">
      <div className={classes.header}>
        <span className={classes.iconBox}>
          <FontAwesomeIcon icon={faDollarSign} />
        </span>
        <Typography component="h2" className={classes.title}>
          Revenue summary
        </Typography>
        <ButtonV2
          styleType="primary"
          className={classes.createBtn}
          data-testid="create-funding-request"
          trackAs="create_funding_request"
          icon={
            <FontAwesomeIcon icon={faSparkles} className={classes.createIcon} />
          }
          onClick={askAi(POTENTIAL_FUNDING_PROMPT)}
        >
          {CREATE_REQUEST_LABEL}
        </ButtonV2>
      </div>
      <div className={classes.metrics}>
        <Metric
          label="Approved funding"
          metric={approved}
          currency={symbol}
          loading={loading}
          onAskAi={askAi(APPROVED_FUNDING_PROMPT, 'approved_funding_metric')}
          tooltip={`${approved.count} funding requests were approved worth ${symbol}${formatAmount(
            approved.amount
          )}.`}
        />
        <Metric
          label="Pending funding"
          metric={pending}
          currency={symbol}
          loading={loading}
          onAskAi={askAi(PENDING_FUNDING_PROMPT, 'pending_funding_metric')}
          tooltip={`${pending.count} funding requests are pending worth ${symbol}${formatAmount(
            pending.amount
          )}.`}
        />
        <Metric
          label="Potential funding"
          metric={potential}
          currency={symbol}
          loading={loading}
          onAskAi={askAi(POTENTIAL_FUNDING_PROMPT, 'potential_funding_metric')}
          tooltip={`${potential.count} potential funding opportunities worth ${symbol}${formatAmount(
            potential.amount
          )}.`}
        />
      </div>
    </section>
  )
}

export default RevenueSummary
